import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  return (
    <>
    <SEO 
      title="Privacy Policy"
      description="Read House of Deepak's Privacy Policy. Learn how we collect, use, protect, and manage your personal information and data security practices."
      keywords="privacy policy, data protection, personal information, data security, privacy practices, House of Deepak privacy"
    />
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-serif font-light text-deep-brown mb-6">
                Privacy Policy
              </h1>
              <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
                Last updated: September 30, 2025
              </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-8 md:p-12 space-y-8">
                
                {/* Introduction */}
                <section>
                  <h2 className="text-2xl font-serif font-light text-deep-brown mb-4">
                    Your Privacy Matters to Us
                  </h2>
                  <p className="text-warm-gray leading-relaxed">
                    At House of Deepak, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or make a purchase.
                  </p>
                </section>

                {/* Information We Collect */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    1. Information We Collect
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-deep-brown mb-2">Personal Information</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside ml-4">
                        <li>Name, email address, phone number</li>
                        <li>Billing and shipping addresses</li>
                        <li>Payment information (processed securely through third-party providers)</li>
                        <li>Account preferences and order history</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-deep-brown mb-2">Automatically Collected Information</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside ml-4">
                        <li>IP address, browser type, and device information</li>
                        <li>Website usage data and navigation patterns</li>
                        <li>Cookies and similar tracking technologies</li>
                        <li>Location data (with your consent)</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* How We Use Your Information */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    2. How We Use Your Information
                  </h3>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>Process and fulfill your orders and payments</li>
                    <li>Communicate with you about your orders and account</li>
                    <li>Provide customer support and respond to inquiries</li>
                    <li>Send promotional emails and marketing communications (with your consent)</li>
                    <li>Improve our website, products, and services</li>
                    <li>Prevent fraud and ensure website security</li>
                    <li>Comply with legal obligations</li>
                  </ul>
                </section>

                {/* Information Sharing */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    3. How We Share Your Information
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                  </p>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li><strong>Service Providers:</strong> With trusted third-party service providers who help us operate our business (payment processors, shipping companies, email services)</li>
                    <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
                    <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of business assets</li>
                    <li><strong>With Your Consent:</strong> When you explicitly agree to share your information</li>
                  </ul>
                </section>

                {/* Data Security */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    4. Data Security
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                  </p>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li>SSL encryption for data transmission</li>
                    <li>Secure servers and databases</li>
                    <li>Regular security assessments and updates</li>
                    <li>Limited access to personal information on a need-to-know basis</li>
                    <li>Employee training on data protection practices</li>
                  </ul>
                </section>

                {/* Cookies */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    5. Cookies and Tracking Technologies
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    We use cookies and similar technologies to enhance your browsing experience and analyze website usage. Types of cookies we use:
                  </p>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li><strong>Essential Cookies:</strong> Required for website functionality and security</li>
                    <li><strong>Performance Cookies:</strong> Help us understand how visitors interact with our website</li>
                    <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                    <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with your consent)</li>
                  </ul>
                  <p className="text-warm-gray leading-relaxed mt-3">
                    You can control cookie settings through your browser preferences, though disabling certain cookies may affect website functionality.
                  </p>
                </section>

                {/* Your Rights */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    6. Your Rights and Choices
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    You have the following rights regarding your personal information:
                  </p>
                  <ul className="text-warm-gray leading-relaxed space-y-2 list-disc list-inside">
                    <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                    <li><strong>Correction:</strong> Update or correct inaccurate personal information</li>
                    <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                    <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                    <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                    <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                  </ul>
                </section>

                {/* Third-Party Links */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    7. Third-Party Links
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    Our website may contain links to third-party websites or services (such as social media platforms or payment providers). We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
                  </p>
                </section>

                {/* Children's Privacy */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    8. Children's Privacy
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately so we can delete such information.
                  </p>
                </section>

                {/* International Users */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    9. International Data Transfers
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    If you are accessing our website from outside India, please note that your information may be transferred to, stored, and processed in India. By using our website, you consent to such transfers in accordance with this Privacy Policy.
                  </p>
                </section>

                {/* Updates to Policy */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    10. Updates to This Privacy Policy
                  </h3>
                  <p className="text-warm-gray leading-relaxed">
                    We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of material changes by posting the updated policy on our website with a new "last updated" date. Your continued use of our website after such changes constitutes acceptance of the updated policy.
                  </p>
                </section>

                {/* Contact Information */}
                <section className="bg-cream p-6 rounded-lg">
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    Contact Us About Privacy
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    If you have any questions about this Privacy Policy or how we handle your personal information, please contact us:
                  </p>
                  <div className="text-warm-gray space-y-1">
                    <p><strong>Email:</strong> privacy@houseofdeepak.in</p>
                    <p><strong>Phone:</strong> +91 9167602861</p>
                    <p><strong>Address:</strong> I-333, Saurabh Vihar, Badarpur-110044</p>
                  </div>
                  <p className="text-warm-gray leading-relaxed mt-3 text-sm">
                    We will respond to your inquiry within 30 days of receipt.
                  </p>
                </section>

              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;